<template>
  <v-dialog v-model="showWizard" persistent max-width="800px">
    <v-card>
      <v-card-title>
        <span class="text-h5">Agent Wizard</span>
      </v-card-title>
      <v-card-text>
        <v-stepper v-model="currentStep" alt-labels>
          <v-stepper-header>
            <v-stepper-item :complete="currentStep > 1" :editable="currentStep > 1" :value="1" title="Provider" color="primary"></v-stepper-item>
            <v-divider></v-divider>
            <v-stepper-item :complete="currentStep > 2" :editable="currentStep > 2" :value="2" title="Model" color="primary"></v-stepper-item>
            <v-divider></v-divider>
            <v-stepper-item :complete="currentStep > 3" :editable="currentStep > 3" :value="3" title="Details" color="primary"></v-stepper-item>
            <v-divider></v-divider>
            <v-stepper-item :complete="currentStep > 4" :editable="currentStep > 4" :value="4" title="Tools" color="primary"></v-stepper-item>
            <v-divider></v-divider>
            <v-stepper-item :complete="currentStep > 5" :editable="currentStep > 5" :value="5" title="Prompt" color="primary"></v-stepper-item>
            <v-divider></v-divider>
            <v-stepper-item :value="6" title="Review"></v-stepper-item>
          </v-stepper-header>

          <v-stepper-window>
            <v-stepper-window-item :value="1">
              <v-form v-model="isProviderFormValid" class="pt-2" autocomplete="off">
                <input id="username" autocomplete="username" type="text" style="display:none">
                <input id="password" autocomplete="new-password" type="password" style="display:none">
                <v-combobox
                  id="autofill-off-provider"
                  name="autofill-off-provider"
                  ref="providerCombobox"
                  v-model="providerKey"
                  :items="allProviders"
                  label="Select a Provider"
                  variant="outlined"
                  density="compact"
                  :rules="[v => !!v || 'Provider is required']"
                  autocomplete="off"
                ></v-combobox>
                <v-text-field
                  id="autofill-off-url"
                  ref="providerUrlField"
                  v-if="isCustomProvider"
                  v-model="providerUrl"
                  label="Provider URL"
                  variant="outlined"
                  density="compact"
                  :rules="[v => !!v || 'URL is required for custom providers']"
                  autocomplete="off"
                ></v-text-field>
                <v-text-field
                  id="autofill-off-api"
                  ref="providerApiKeyField"
                  v-if="!providerExists"
                  v-model="providerApiKey"
                  label="API Key"
                  type="password"
                  variant="outlined"
                  density="compact"
                  :rules="[v => !isCustomProvider && !providerExists ? !!v : true || 'API Key is required']"
                  autocomplete="off"
                ></v-text-field>
              </v-form>
            </v-stepper-window-item>
            <v-stepper-window-item :value="2">
              <v-form v-model="isModelFormValid">
                <v-radio-group v-model="modelSetupType">
                  <v-radio :label="recommendedModelLabel" value="recommended"></v-radio>
                  <v-radio label="Enter Model Name" value="custom"></v-radio>
                </v-radio-group>
                <v-combobox
                  v-if="modelSetupType === 'custom'"
                  v-model="modelName"
                  :items="providerModels"
                  label="Model Name (e.g. gpt-4.1, claude-sonnet-4-0)"
                  variant="outlined"
                  density="compact"
                  :rules="[v => !!v || 'Model name is required']"
                ></v-combobox>
              </v-form>
            </v-stepper-window-item>
            <v-stepper-window-item :value="3">
              <v-form v-model="isAgentDetailsFormValid">
                <v-textarea
                  v-model="agentTask"
                  class="pt-1"
                  label="What should this agent do?"
                  placeholder="e.g., 'Analyze the following rss feeds and summarize the 5 most interesting articles...', or 'The user will provide a public company name or stock ticker, fetch the latest financial data, and provide an analysis...'"
                  variant="outlined"
                  density="compact"
                  rows="5"
                  :rules="[v => !!v || 'This field is required']"
                ></v-textarea>
              </v-form>
            </v-stepper-window-item>
            <v-stepper-window-item :value="4">
              <div v-if="toolsLoading">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
                <p class="ml-4 d-inline">Asking model if tools are needed...</p>
              </div>
              <div v-else>
                <p>Based on your request, the model recommends {{ toolRecommendation }}.</p>
                <!-- UI to potentially override the recommendation could be added here -->
              </div>
            </v-stepper-window-item>
            <v-stepper-window-item :value="5">
              <div v-if="promptLoading">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
                <p class="ml-4 d-inline">Generating system prompt...</p>
              </div>
              <v-textarea
                v-else
                class="pt-1"
                v-model="generatedPrompt"
                label="Generated System Prompt"
                variant="outlined"
                density="compact"
                rows="10"
              ></v-textarea>
            </v-stepper-window-item>
            <v-stepper-window-item :value="6">
              <v-card-text class="pt-2">
                <p class="text-h6">Review your new Agent</p>
                <v-divider class="my-2"></v-divider>
                <v-text-field v-model="agentName" label="Agent Name" variant="outlined" density="compact"></v-text-field>
                <v-text-field v-model="agentDescription" label="Agent Description" variant="outlined" density="compact"></v-text-field>
                <p><strong>Provider:</strong> {{ wizardState.providerKey }}</p>
                <p><strong>Model:</strong> {{ wizardState.modelName }}</p>
                <p class="mt-2"><strong>System Prompt:</strong></p>
                <v-sheet border rounded class="pa-2 system-prompt-sheet">{{ wizardState.systemPrompt }}</v-sheet>
              </v-card-text>
            </v-stepper-window-item>
          </v-stepper-window>
        </v-stepper>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="blue-darken-1" variant="text" @click="closeWizard">Cancel</v-btn>
        <v-btn v-if="currentStep > 1" color="blue-darken-1" variant="text" @click="currentStep--">Back</v-btn>
        <v-btn color="blue-darken-1" variant="text" @click="nextStep">Next</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapGetters } from 'vuex';
import { ComponentPublicInstance, reactive, VueElement, VueElementConstructor } from 'vue';
import { ProviderConfig, ProviderKey } from '@/types/providers';
import { ModelConfig, UserModelConfig } from '@/types/models';
import { AgentConfig, AgentToolOptions } from '@/types/agents';
import { newId } from '@/utils/general';
import { Tool, ToolMethod } from '@/types/mcp';

interface WizardState {
  providerKey: string | null;
  modelName: string | null;
  agentTask: string;
  toolRecommendation: string;
  systemPrompt: string;
  toolOptions: AgentToolOptions | undefined;
}

@Options({
  computed: {
    ...mapGetters(['showAgentWizard', 'builtInProviders', 'modelDefaults', 'modelConfigByName', 'providerConfigs', 'allProviderModels'])
  },
  watch: {
    async currentStep(newStep) {
      if (newStep === 4) {
        await this.evaluateToolNeeds();
      } else if (newStep === 5) {
        await this.generateAgentPrompt();
      } else if (newStep === 6) {
        this.prepareReviewStep();
      }
    }
  }
})
export default class AgentWizardDialog extends Vue {
  public showAgentWizard!: boolean;
  public modelConfigByName!: (name: string) => UserModelConfig;
  public builtInProviders!: string[];
  public providerConfigs!: Record<string, ProviderConfig>;
  public allProviderModels!: Record<string, any[]>;
  public modelDefaults!: any;
  public currentStep = 1;

  // Form state for Step 1
  public providerKey: ProviderKey | null = null;
  public providerUrl = '';
  public providerApiKey = '';
  public isProviderFormValid = false;

  // Form state for Step 2
  public modelSetupType = 'recommended';
  public modelName = '';
  public isModelFormValid = false;

  // Form state for Step 3
  public agentTask = '';
  public isAgentDetailsFormValid = false;

  // Form state for Step 4
  public toolsLoading = false;
  public toolRecommendation = 'no tools';

  // Form state for Step 5
  public promptLoading = false;
  public generatedPrompt = '';

  // Form state for Step 6
  public agentName = '';
  public agentDescription = '';

  private recommendedModels: { [key: string]: string } = {
    openai: 'o4-mini',
    anthropic: 'claude-3-7-sonnet-latest',
    google: 'models/gemini-2.5-pro',
    groq: 'llama-3.3-70b-versatile'
  };

  // This object will hold the data across all steps of the wizard.
  public wizardState: WizardState = reactive({
    providerKey: null,
    modelName: null,
    agentTask: '',
    toolRecommendation: '',
    systemPrompt: '',
    toolOptions: undefined
  });

  get showWizard(): boolean {
    return this.showAgentWizard;
  }

  set showWizard(value: boolean) {
    if (!value) {
      this.closeWizard();
    }
  }

  get isCustomProvider(): boolean {
    return !!this.providerKey && !this.builtInProviders.includes(this.providerKey);
  }

  get allProviders() {
    const existing = Object.keys(this.providerConfigs);
    const builtIn = this.builtInProviders.filter(p => !existing.includes(p));
    return [...existing, ...builtIn];
  }

  get providerExists(): boolean {
    if (!this.providerKey) return false;
    return this.providerKey in this.providerConfigs;
  }

  get recommendedModelLabel(): string {
    if (this.providerKey && this.recommendedModels[this.providerKey]) {
      return `Use Recommended Model (${this.recommendedModels[this.providerKey]})`;
    }
    return 'Use Recommended Model';
  }

  get providerModels(): string[] {
    if (this.providerKey && this.allProviderModels[this.providerKey]) {
      return this.allProviderModels[this.providerKey].map(m => m.id);
    }
    return [];
  }

  mounted() {
    const providerApiKeyFieldRef = this.$refs.providerApiKeyField as any
    if (providerApiKeyFieldRef) {
      const providerApiKeyField = (providerApiKeyFieldRef as ComponentPublicInstance).$el as HTMLElement
      const providerApiKeyFieldInput = providerApiKeyField.querySelector('input')
      if (providerApiKeyFieldInput) {
        providerApiKeyFieldInput.setAttribute('data-1p-ignore', '')
      }
    }
  }

  async nextStep() {
    switch (this.currentStep) {
      case 1:
        await this.handleProviderNext();
        break;
      case 2:
        await this.handleModelNext();
        break;
      case 3:
        await this.handleAgentDetailsNext();
        break;
      case 4:
        this.wizardState.toolRecommendation = this.toolRecommendation;
        this.currentStep++;
        break;
      case 5:
        this.wizardState.systemPrompt = this.generatedPrompt;
        this.currentStep++;
        break;
      case 6:
        await this.createAgent();
        break;
      default:
        this.currentStep++;
    }
  }

  async evaluateToolNeeds() {
    this.toolsLoading = true;

    const modelConfig = this.modelConfigByName(this.wizardState.modelName!);
    if (!modelConfig) {
      console.error(`Wizard: Could not find model config for name: ${this.wizardState.modelName}`);
      this.toolRecommendation = 'an error occurred: model configuration not found.';
      this.toolsLoading = false;
      return;
    }
    
    const tools: Tool[] = await this.$store.dispatch('listTools');
    if (!tools || tools.length === 0) {
      this.toolRecommendation = 'no tools are available';
      this.toolsLoading = false;
      return;
    }

    const formattedTools = tools.flatMap(toolObject => 
      Object.values(toolObject).flat().map(method => 
        `${method.name}: ${method.description}`
      )
    ).join('\n');
    const prompt = `Based on the user's request: "${this.wizardState.agentTask}", would any of the following tools be useful? Respond with a comma-separated list of the most useful tool names, or "no" if none are applicable. Tools:\n${formattedTools}`;

    const response = await this.$store.dispatch('completionRequest', {
      messages: [{ role: 'user', content: prompt }],
      modelConfig: modelConfig.config
    });

    if (response && response.content) {
      this.toolRecommendation = response.content;
    } else {
      this.toolRecommendation = 'an error occurred while checking for tools';
    }
    
    this.wizardState.toolRecommendation = this.toolRecommendation;
    this.toolsLoading = false;
  }

  prepareReviewStep() {
    this.agentName = `${this.wizardState.modelName!.split('/').pop()}-agent`;
    this.agentDescription = `An agent to help with: ${this.wizardState.agentTask.substring(0, 50)}...`;
  }

  async createAgent() {
    const { systemPrompt, toolRecommendation } = this.wizardState;

    const agentConfig: AgentConfig = {
      id: newId(),
      timestamp: Date.now(),
      name: this.agentName,
      description: this.agentDescription,
      systemPrompt: systemPrompt!,
      model: this.wizardState.modelName!,
      context: [],
      temperature: 0.2,
      maxTokens: -1,
      modelOptions: this.modelDefaults,
      toolOptions: undefined
    };

    if (toolRecommendation && toolRecommendation.toLowerCase() !== 'no' && toolRecommendation.toLowerCase() !== 'no tools are available' && !toolRecommendation.toLowerCase().includes('error')) {
      const fullToolsList: Tool[] = await this.$store.dispatch('listTools');
      const recommendedFunctions = toolRecommendation.split(',').map(t => t.trim());
      
      const toolOptions: AgentToolOptions = {
        systemTools: [],
        userTools: [],
        selectedFunctions: {}
      };

      const serverFunctionMap: Record<string, string[]> = {};

      for (const funcName of recommendedFunctions) {
        for (const tool of fullToolsList) {
          const serverName = Object.keys(tool)[0];
          const methods = tool[serverName];
          if (methods.some(m => m.name === funcName)) {
            if (!serverFunctionMap[serverName]) {
              serverFunctionMap[serverName] = [];
            }
            serverFunctionMap[serverName].push(funcName);
            break; 
          }
        }
      }
      
      toolOptions.userTools = Object.keys(serverFunctionMap);
      toolOptions.selectedFunctions = serverFunctionMap;
      agentConfig.toolOptions = toolOptions;
    }

    const success = await this.$store.dispatch('setAgentConfig', agentConfig);
    if (success) {
      this.$store.dispatch('updateSnackbar', { text: `Agent "${this.agentName}" created successfully!` });
      this.closeWizard();
    }
  }

  async generateAgentPrompt() {
    this.promptLoading = true;

    const agentTask = this.wizardState.agentTask;
    const toolInfo = this.wizardState.toolRecommendation;
    
    const generatorPrompt = `Based on the user's goal to "${agentTask}" and the tool recommendation "${toolInfo}", create a detailed system prompt for an AI agent.`;
    
    const modelConfig = this.modelConfigByName(this.wizardState.modelName!);

    const response = await this.$store.dispatch('generatePrompt', {
      prompt: generatorPrompt,
      modelConfig: modelConfig.config
    });

    if (typeof response === 'string') {
      this.generatedPrompt = response;
      this.wizardState.systemPrompt = response;
    } else {
      this.generatedPrompt = 'Error generating prompt.';
    }
    
    this.promptLoading = false;
  }

  async handleAgentDetailsNext() {
    if (!this.isAgentDetailsFormValid) return;
    // Store the user's request for later steps
    this.wizardState.agentTask = this.agentTask;
    this.currentStep++;
  }

  async handleModelNext() {
    if (!this.isModelFormValid) return;

    const providerKey = this.wizardState.providerKey;
    const model = this.modelSetupType === 'recommended'
      ? this.recommendedModels[providerKey!]
      : this.modelName;

    const modelConfigPayload: Omit<ModelConfig, 'id' | 'timestamp' | 'url' | 'apiKey' | 'response' | 'hash'> = {
      name: model, // Use the model id as the initial name
      description: `Configuration for ${model}`,
      providerKey: providerKey!,
      model: model,
      options: this.modelDefaults
    };

    const success = await this.$store.dispatch('setModelConfig', modelConfigPayload);
    if (success) {
      // Store model name for next step
      this.wizardState.modelName = model;
      this.currentStep++;
    }
  }

  async handleProviderNext() {
    if (!this.isProviderFormValid) return;

    if (this.providerExists) {
      this.wizardState.providerKey = this.providerKey;
      this.currentStep++;
      return;
    }

    const config: ProviderConfig = {
      providerKey: this.providerKey!,
      url: this.isCustomProvider ? this.providerUrl : undefined,
      apiKey: this.providerApiKey
    };

    const success = await this.$store.dispatch('addProvider', config);
    if (success) {
      this.wizardState.providerKey = this.providerKey;
      this.currentStep++;
    }
  }

  closeWizard() {
    this.$store.dispatch('toggleAgentWizard', false);
    // Reset state
    this.currentStep = 1;
    Object.assign(this.wizardState, {
      providerKey: null,
      modelName: null,
      agentTask: '',
      toolRecommendation: '',
      systemPrompt: '',
      toolOptions: undefined
    });
    // Reset form fields
    this.providerKey = null;
    this.providerUrl = '';
    this.providerApiKey = '';
    this.isProviderFormValid = false;
    this.modelSetupType = 'recommended';
    this.modelName = '';
    this.isModelFormValid = false;
    this.agentTask = '';
    this.isAgentDetailsFormValid = false;
    this.toolsLoading = false;
    this.toolRecommendation = 'no tools';
    this.promptLoading = false;
    this.generatedPrompt = '';
    this.agentName = '';
    this.agentDescription = '';
  }
}
</script>
<style lang="scss" scoped>
.system-prompt-sheet {
  max-height: 600px;
  overflow-y: auto;
  white-space: pre-wrap;
}
</style>
